import { CommonModule,formatCurrency,formatDate,getCurrencySymbol } from '@angular/common';
import {
    Component,
    DEFAULT_CURRENCY_CODE,
    EventEmitter,
    Inject,
    Input,
    LOCALE_ID,
    OnInit,
    Output,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, of } from 'rxjs';
import { filter,first,map,switchMap,tap } from 'rxjs/operators';
import { MaterialToSupplierDataService } from '../../../dave-data-module/services/material-to-supplier-data.service';
import { State } from '../../../dave-data-module/State';
import { getMaterialGroups } from '../../../dave-data-module/State/selectors/material-group.selectors';
import { DaveListCardModule } from '../../../dave-list-card/dave-list-card.module';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { SelectSearchOption } from '../../../dave-utils-module/select-search/components/select-search/select-search.component';
import { isNotNullOrUndefined,MathRound } from '../../../helper/helper';
import { CustomLabelService } from '../../../services/custom-label/custom-label.service';
import { EditMaterialToSupplierComponentDialogReturnData,EditMaterialToSupplierDialogComponent,EditMaterialToSupplierDialogComponentDialogData } from '../edit-material-to-supplier-dialog/edit-material-to-supplier-dialog.component';
import { FormControl, FormGroup } from '@angular/forms';
import { MaterialToSupplierEntity } from '../../../dave-data-module/entities/materialToSupplier.entity';
import { CustomerDataService } from '../../../dave-data-module/services/customer-data.service';
import { Dictionary } from '@ngrx/entity';
import { CustomerEntity } from '../../../dave-data-module/entities/customer.entity';


interface MaterialToSupplierTableData {
    SupplierName: string;
    Date: string;
    Cost: string;
    cost_as_float: number;
    ListPrice: string;
    listPrice_as_float: number;
    SellPrice: string;
    sellPrice_as_float: number;
    UpdatedAt: Date;
    ArticleNumber: string;
    SupplierId: number;
    MaterialGroupId: number;
    MaterialId: number;
    updatedAtForSort: number;
    Icon?: boolean;
    Name: string;
    Description: string;
}

@Component({
    selector: 'app-material-to-supplier-list-card[materialId]',
    standalone: true,
    imports: [CommonModule, AppButtonModule, DaveListCardModule, FontAwesomeModule, MatTooltipModule],
    templateUrl: './material-to-supplier-list-card.component.html',
    styleUrls: ['./material-to-supplier-list-card.component.scss'],
})
export class MaterialToSupplierListCardComponent  {
    protected MaterialId$ = new BehaviorSubject<number>(null);
    @Input() set materialId(id: number) {
        if (this.MaterialId$.value !== id) {
            this.MaterialId$.next(id);
        }
    }
    @Input() materialEKForm: FormControl<number>;
    @Input() showSupplierButton: boolean;
    @Input() showAcceptPriceButton: boolean;
    @Output() M2SChange = new EventEmitter<MaterialToSupplierEntity>();

    protected supplierLabel$: Observable<string> = this.cls.getSingle$('Supplier');
    public MaterialToSupplierTableDataLoading$ = new BehaviorSubject(false);
    public MaterialToSupplierTableData$ = this.MaterialId$.pipe(
        filter(isNotNullOrUndefined),
        tap(() => this.MaterialToSupplierTableDataLoading$.next(true)),
        switchMap((mId) =>
            this.materialToSupplierDataService.getMaterialToSupplierByMaterialId$(mId).pipe(
                switchMap((supplierEntries) =>
                    combineLatest([
                        of(supplierEntries),
                        this.customerDataService.GetCustomersDict$(
                            supplierEntries.map((s) => s.SupplierId).filter(isNotNullOrUndefined),
                        ),
                        this.supplierLabel$,
                    ]),
                ),
                tap(() => this.MaterialToSupplierTableDataLoading$.next(false)),
                map(([supplierEntries, customersRaw, supplierLabel]) => {
                    const customers: Dictionary<CustomerEntity> = customersRaw ?? {};
                    if (!supplierEntries.length) {
                        return new MatTableDataSource<MaterialToSupplierTableData>([]);
                    }
                    return new MatTableDataSource<MaterialToSupplierTableData>(
                        supplierEntries.map<MaterialToSupplierTableData>((s: MaterialToSupplierEntity) => {
                            const data = {
                                SupplierId: s.SupplierId,
                                MaterialGroupId: s.MaterialGroupId,
                                SupplierName: s.SupplierId ? (customers[s.SupplierId]?.Name ?? 'Unbekannt') : '',
                                Date: formatDate(s.UpdatedAt, 'shortDate', this.locale),
                                Cost: isNotNullOrUndefined(s.Cost) ? formatCurrency(s.Cost / 100, this.locale, getCurrencySymbol(this.defaultCurrencyCode, 'narrow')) : null,
                                cost_as_float: s.Cost,
                                SellPrice: isNotNullOrUndefined(s.SellPrice) ? formatCurrency(s.SellPrice / 100, this.locale, getCurrencySymbol(this.defaultCurrencyCode, 'narrow')) : null,
                                sellPrice_as_float: s.SellPrice,
                                ListPrice: isNotNullOrUndefined(s.ListPrice) ? formatCurrency(s.ListPrice / 100, this.locale, getCurrencySymbol(this.defaultCurrencyCode, 'narrow')) : null,
                                listPrice_as_float: s.ListPrice,
                                UpdatedAt: s.UpdatedAt,
                                ArticleNumber: s.ArticleNumber,
                                MaterialId: s.MaterialId,
                                updatedAtForSort: s.CreatedAt.getTime(),
                                UsedInformation: s.UsedInformation,
                                Name: s.Name,
                                Description: s.Description,
                            };
                            if (this.showSupplierButton) {
                                return {
                                    ...data,
                                    suffixButton: {
                                        icon: 'arrow-right',
                                        tooltip: supplierLabel + ' setzen',
                                        onClick: (v) => {
                                            this.M2SChange.emit(s);
                                        },
                                    },
                                };
                            }
                            if (this.materialEKForm || this.showAcceptPriceButton) {
                                return {
                                    ...data,
                                    suffixButton: {
                                        icon: 'arrow-right',
                                        tooltip: 'EK Preis übernehmen',
                                        onClick: (v) => {
                                            if (this.materialEKForm) {
                                                this.materialEKForm.setValue(s.Cost / 100);
                                            }
                                            this.M2SChange.emit(s);
                                        },
                                    },
                                };
                            } else {
                                return data;
                            }
                        }),
                    );
                }),
                tap(
                    (dataSource) =>
                        (dataSource.sortingDataAccessor = (object, key) => {
                            switch (key) {
                                case 'Date':
                                    return object.updatedAtForSort;
                                case 'Cost':
                                    return object.cost_as_float;
                                default:
                                    return object[key].trim().toLowerCase();
                            }
                        }),
                ),
            ),
        ),
    );

    refreshTable() {
        this.MaterialId$.next(this.MaterialId$.value);
    }


    constructor(
        private store: Store<State>,
        private materialToSupplierDataService: MaterialToSupplierDataService,
        private dialog: MatDialog,
        protected cls: CustomLabelService,
        @Inject(LOCALE_ID) private locale: string,
        @Inject(DEFAULT_CURRENCY_CODE) private defaultCurrencyCode: string,
        private customerDataService: CustomerDataService,
    ) {}

    async onAddSupplier(s: MaterialToSupplierTableData) {
        if (s != null) {
            const customer = await this.findCustomerById(s.SupplierId);
            const productGroup = this.findProductGroupById(s.MaterialGroupId);
            this.dialog
                .open<EditMaterialToSupplierDialogComponent, EditMaterialToSupplierDialogComponentDialogData, EditMaterialToSupplierComponentDialogReturnData>(EditMaterialToSupplierDialogComponent, {
                    ...EditMaterialToSupplierDialogComponent.DefaultConfig,
                    data: {
                        name: s.Name,
                        description: s.Description,
                        customer: customer,
                        articlenumber: s.ArticleNumber,
                        cost: s.cost_as_float,
                        listPrice: s.listPrice_as_float,
                        sellPrice: s.sellPrice_as_float,
                        productGroup: productGroup,
                        materialId: s.MaterialId,
                    },
                })
                .afterClosed()
                .subscribe((result) => {});
        } else {
            firstValueFrom(this.MaterialId$).then((materialId) => {
                if (materialId) {
                    this.dialog
                        .open<EditMaterialToSupplierDialogComponent, EditMaterialToSupplierDialogComponentDialogData, EditMaterialToSupplierComponentDialogReturnData>(EditMaterialToSupplierDialogComponent, {
                            ...EditMaterialToSupplierDialogComponent.DefaultConfig,
                            data: {
                                materialId: materialId,
                            },
                        })
                        .afterClosed()
                        .subscribe((result) => {});
                }
            });
        }
    }
    protected async findCustomerById(id: number): Promise<SelectSearchOption<{ Id: number }> | undefined> {
        if (!id) {
            return undefined;
        }
        const customer = await firstValueFrom(this.customerDataService.GetSingleCustomer$(id));
        return customer ? { Id: customer.Id, optionLabel: customer.DisplayName } : undefined;
    }
    findProductGroupById(id: number): SelectSearchOption<{ Id: number }> {
        let productGroup: SelectSearchOption<{ Id: number }>;
        this.store
            .select(getMaterialGroups)
            .pipe(
                first(),
                map((groups) => groups.find((group) => group.Id === id)),
            )
            .subscribe((foundGroup) => {
                if (foundGroup) {
                    productGroup = { Id: foundGroup.Id, optionLabel: foundGroup.Name };
                }
            });
        return productGroup;
    }


}
